import { useState, useCallback } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { FaPlus } from 'react-icons/fa6';

import {
  checkUrl,
  saveStdioConnection,
  saveCustomConnection,
  deleteConnection,
  recheckConnection,
} from './api';
import AddConnection from './AddConnection';
import Spinner from './Spinner';
import ConnectionItem from './ConnectionItem';
import { useMCP } from '../../context/McpProvider';
import { DataState } from '../../context/DataProvider';
import GlobalConnectionList from './GlobalConnectionList';
import { API_URL } from '../../services/api';
import Modal from '../base/Modal';

const McpConnectionManager = () => {
  const {
    connections,
    setConnections,
    isLoading,
    error,
    loadConnections,
    refreshConnections,
    globalConnections,
    checkResult,
    setCheckResult,
  } = useMCP();
  const { userGoogleId: accessToken } = DataState();
  const [expandedConnectionId, setExpandedConnectionId] = useState(null);
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [openAddConnection, setOpenAddConnection] = useState(false);

  const handleCheckUrl = async (url, transport_type) => {
    setIsCheckingUrl(true);
    setCheckResult(null);
    try {
      const response = await checkUrl(accessToken, url, transport_type);
      setCheckResult(response);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : JSON.stringify(err));
    } finally {
      setIsCheckingUrl(false);
    }
  };

  const handleInitiateOauth = async (data) => {
    // data contains { origin, connection_name, sse_url }
    const frontendOrigin = window.location.origin;

    let isComplete = false;
    // A reference to the popup window
    let popup = null;
    // A reference to the interval that checks if the popup is closed
    let popupPoller = null;

    try {
      const params = new URLSearchParams({
        accessToken: accessToken,
        apiBaseUrl: API_URL,
        origin: data.origin,
        connection_name: data.connection_name,
        sse_url: data.sse_url,
        frontend_origin: frontendOrigin,
        transport_type: data.transport_type || 'sse', // Default to 'sse' if not provided
      });
      const expectedOrigin = new URL(API_URL).origin;

      const authStartUrl = `/mcp-auth-start.html?${params.toString()}`;

      // --- 1. OPEN POPUP IMMEDIATELY ---
      popup = window.open(authStartUrl, 'mcp_oauth', 'width=600,height=700');

      if (!popup) {
        toast.error('Popup was blocked. Please allow popups for this site.');
        return;
      }

      // --- 2. CLEANUP FUNCTION ---
      // A single function to remove the listener and the poller to avoid code duplication.
      const cleanup = () => {
        if (popupPoller) {
          clearInterval(popupPoller);
          popupPoller = null;
        }
        window.removeEventListener('message', receiveMessage);
      };

      // --- 3. MESSAGE LISTENER ---
      // Handles the final success or error message from the backend callback.
      const receiveMessage = (event) => {
        // Security: Only accept messages from our own app's origin.

        if (
          event.origin !== window.location.origin &&
          event.origin !== expectedOrigin
        ) {
          return;
        }

        const { status, error } = event.data;
        // Ensure it's a message from our auth flow
        if (status) {
          // ✅ 2. Set the flag to true immediately.
          // This signals that the popup is closing intentionally.
          isComplete = true;
          if (status === 'success') {
            toast.success('Connection successful! Refreshing list...');
            refreshConnections(); // Refresh the connections list
          } else if (status === 'error') {
            toast.error(
              `Connection failed: ${error || 'An unknown error occurred.'}`,
            );
          }

          // Once we have a definitive result, clean up and close the popup.
          cleanup();
          if (popup && !popup.closed) {
            popup.close();
          }
        }
      };

      window.addEventListener('message', receiveMessage);

      // --- 4. POPUP CLOSE DETECTOR ---
      // This poller checks if the user has manually closed the popup window.
      popupPoller = setInterval(() => {
        if (popup && popup.closed && !isComplete) {
          toast.error('Connection process cancelled.');
          cleanup(); // Important: clean up the listener and poller
        }
      }, 500);
    } catch (err) {
      toast.error(
        'A client-side error occurred while starting the connection.',
      );
    }
  };

  const handleSaveCustom = async (data) => {
    try {
      await saveCustomConnection(accessToken, data);
      toast.success('Custom connection saved!');
      loadConnections();
      setCheckResult(null); // Reset the add form
    } catch (err) {
      toast.error(
        'Failed to save custom connection: ' +
          (err.response?.detail || err.message),
      );
    }
  };

  // ✅ NEW: Handler for saving STDIO connections
  const handleSaveStdio = async (data) => {
    try {
      await saveStdioConnection(accessToken, data);
      toast.success('STDIO connection saved!');
      refreshConnections(); // Refresh list to show the new connection
    } catch (err) {
      toast.error(
        'Failed to save STDIO connection: ' +
          (err.response?.detail || err.message),
      );
    }
  };

  const handleDeleteConnection = useCallback(async (id) => {
    await deleteConnection(accessToken, id);
    setConnections((prev) => prev.filter((c) => c.id !== id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecheckConnection = useCallback(
    async (id) => {
      await recheckConnection(accessToken, id);
      loadConnections(); // Reload all to get updated status
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadConnections],
  );

  // NEW: Handler to toggle connections, ensuring only one is open at a time
  const handleToggleConnection = (connectionId) => {
    setExpandedConnectionId((prevId) =>
      prevId === connectionId ? null : connectionId,
    );
  };
  return (
    <div className="space-y-4 text-gray-300">
      {/* NEW: Render the global connections list before the custom add form */}
      <GlobalConnectionList
        connections={globalConnections}
        onInitiateOauth={handleInitiateOauth}
        onSaveCustom={handleSaveCustom}
      />

      <div>
        {/* NEW: Flex container for the title and refresh button */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="display-0  text-lg font-semibold text-white">
            My MCPs
          </h3>
          <button
            onClick={refreshConnections}
            disabled={isLoading}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Refresh Connections List"
          >
            {/* Show a spinner while loading, otherwise show the refresh icon */}
            {isLoading ? <Spinner size="1rem" /> : <FiRefreshCw />}
          </button>
        </div>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner size="2rem" />
          </div>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <div className="flex flex-wrap gap-x-4 gap-y-4 pb-8 max-sm:flex-col max-sm:items-center">
            {/* Pass the state and handler down to the list */}
            {connections.length > 0 ? (
              connections.map((conn) => (
                <ConnectionItem
                  key={conn.id}
                  connection={conn}
                  isExpanded={expandedConnectionId === conn.id}
                  onToggle={() => handleToggleConnection(conn.id)}
                  onDelete={handleDeleteConnection}
                  onRecheck={handleRecheckConnection}
                />
              ))
            ) : (
              <div className="flex w-full flex-col items-center justify-center text-center">
                <div className="mb-4 text-sm text-zinc-500">
                  No custom MCP added yet.
                </div>

                <button
                  onClick={() => setOpenAddConnection(true)}
                  className="flex items-center gap-2 rounded-lg bg-zinc-200 px-4 py-2 text-sm font-medium text-black transition hover:bg-white"
                >
                  <FaPlus size={12} />
                  Add custom MCP
                </button>
              </div>
            )}
            {connections.length > 0 && (
              <button
                onClick={() => setOpenAddConnection(true)}
                className="flex w-fit max-w-xl flex-wrap items-center gap-2 rounded-full border border-zinc-400 bg-white p-1  px-4 transition-all duration-300 dark:bg-zinc-800"
              >
                <FaPlus size={12} />
                Add custom MCP
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={openAddConnection}
        onClose={() => setOpenAddConnection(false)}
        title="Add New Connection"
      >
        <AddConnection
          onCheck={handleCheckUrl}
          onInitiateOauth={handleInitiateOauth}
          onSaveCustom={handleSaveCustom}
          checkResult={checkResult}
          isChecking={isCheckingUrl} // ✅ Pass the new handler
          onSaveStdio={handleSaveStdio}
          onClose={() => setOpenAddConnection(false)}
        />
      </Modal>
    </div>
  );
};

export default McpConnectionManager;
